import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang, IMAGES } from "../i18n";
import { useLenis } from "../hooks/useLenis";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { LeadForm } from "../components/LeadForm";
import { Reveal, SectionLabel, MaskLine } from "../components/Motion";
import { Seo } from "../components/Seo";
import { ArrowLeft, ArrowUpRight, Brain, Link2, Radio, Boxes, ShieldCheck } from "lucide-react";

const ICONS = [Brain, Link2, Radio, Boxes];

export default function PatentLabel() {
  useLenis();
  const { t } = useLang();
  const p = t.patent1;
  const chapters = t.patent.chapters;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="grain relative">
      <Seo page="patent1" />
      <Nav />
      <main>
        {/* HERO */}
        <section data-testid="patent1-hero" className="relative pt-36 md:pt-44 pb-16 md:pb-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-7">
              <Link to="/" data-testid="patent1-back" className="inline-flex items-center gap-2 font-jetbrains text-[11px] tracking-[0.2em] uppercase text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors mb-10">
                <ArrowLeft className="w-4 h-4" /> Amazonico3000
              </Link>
              <SectionLabel>{p.label}</SectionLabel>
              <h1 className="font-cormorant font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[var(--text-primary)]">
                <MaskLine delay={0.1}>{p.title}</MaskLine>
              </h1>
              <Reveal delay={0.25}>
                <p className="font-jetbrains text-[11px] md:text-xs tracking-wider leading-relaxed text-[var(--gold)] mt-6 max-w-2xl uppercase">
                  {p.fullTitle}
                </p>
                <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-6 max-w-2xl">
                  {p.intro}
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.25} className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-[2rem] border border-[var(--border)]">
                <img src={IMAGES.macroTag} alt="Etichetta Intelligente Auto-Evolutiva — smart label multistrato" data-testid="patent1-hero-img" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-primary)]/60 to-transparent" />
                <motion.div
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 2.5, repeat: Infinity }}
                  className="absolute top-6 right-6 flex items-center gap-2 glass rounded-full px-3 py-1.5"
                >
                  <span className="w-2 h-2 rounded-full bg-[var(--green)]" />
                  <span className="font-jetbrains text-[10px] tracking-widest text-[var(--text-primary)]">SCANNING</span>
                </motion.div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* CHAPTERS */}
        <section data-testid="patent1-chapters" className="py-16 md:py-24 bg-[var(--bg-secondary)] border-y border-[var(--border)]">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <SectionLabel>{p.chaptersLabel}</SectionLabel>
            <div className="space-y-4 mt-10">
              {chapters.map((c, i) => {
                const Icon = ICONS[i] || ShieldCheck;
                return (
                  <Reveal key={i} delay={i * 0.08}>
                    <div className="group relative flex gap-6 p-8 rounded-2xl border border-[var(--border)] hover:border-[var(--gold)]/40 bg-[var(--bg-primary)] transition-colors duration-500">
                      <span className="font-jetbrains text-sm text-[var(--gold)] shrink-0">{c.n}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <Icon className="w-5 h-5 text-[var(--green)]" />
                          <h3 className="font-cormorant text-2xl md:text-3xl text-[var(--text-primary)]">{c.t}</h3>
                        </div>
                        <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed">{c.d}</p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* SECTORS CTA */}
        <section data-testid="patent1-sectors" className="py-16 md:py-24">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
            <Reveal>
              <h2 className="font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)]">{p.sectorsTitle}</h2>
              <p className="font-outfit font-light text-[var(--text-secondary)] mt-4 max-w-2xl mx-auto">{p.sectorsSub}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
                <a href="/#sectors" data-testid="patent1-sectors-cta" className="inline-flex items-center gap-3 rounded-full border border-[var(--gold)] px-8 py-4 font-outfit text-xs tracking-[0.2em] uppercase text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-colors duration-300">
                  {p.sectorsCta} <ArrowUpRight className="w-4 h-4" />
                </a>
                <Link to="/moda-lusso" data-testid="patent1-fashion-cta" className="inline-flex items-center gap-3 rounded-full border border-[var(--border)] px-8 py-4 font-outfit text-xs tracking-[0.2em] uppercase text-[var(--text-secondary)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors duration-300">
                  {p.fashionCta} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>

        <LeadForm source="patent-label" interests={p.interests} label={p.label} />
      </main>
      <Footer />
    </div>
  );
}
